/**
 * Users management component for admin panel.
 */

import { useState, useEffect } from 'react';
import { Button, Table, Form, Modal, Badge, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';
import { handleError, AppError } from '@/utils/errorHandler';
import { PAGINATION, TIMEOUTS, INPUT_LIMITS, BADGE_COLORS } from '@/constants';

interface User {
  id: number;
  email: string;
  phone_number?: string;
  is_active: boolean;
  phone_verified: boolean;
  user_group_id?: number;
  created_at: string;
}

interface UserGroup {
  id: number;
  name: string;
  description?: string;
}

interface UsersListResponse {
  users: User[];
  total: number;
  skip: number;
  limit: number;
}

export default function UsersManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const pageSize = PAGINATION.DEFAULT_PAGE_SIZE;

  const [formData, setFormData] = useState({
    email: '',
    phone_number: '',
    user_group_id: undefined as number | undefined,
    is_active: true,
    phone_verified: false,
  });

  // Debounce search term to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, TIMEOUTS.DEBOUNCE_SEARCH);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load users when debounced search term or current page changes
  useEffect(() => {
    const abortController = new AbortController();

    loadUsers(abortController.signal);
    loadGroups();

    // Cleanup: abort pending request if dependencies change or component unmounts
    return () => {
      abortController.abort();
    };
  }, [currentPage, debouncedSearchTerm]);

  const loadUsers = async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const response = await apiClient.getAdminUsers(
        {
          skip: currentPage * pageSize,
          limit: pageSize,
          search: debouncedSearchTerm || undefined,
        },
        { signal }
      );

      if (response.error) {
        // Don't throw error for cancelled requests
        if (response.error === 'Request cancelled') {
          return;
        }
        throw new AppError(response.error, 'LOAD_USERS_FAILED');
      }
      const data = response.data as UsersListResponse;
      setUsers(data.users || []);
      setTotalUsers(data.total || 0);
    } catch (error) {
      // Don't show error toast for cancelled requests
      if (error instanceof AppError && error.message === 'Request cancelled') {
        return;
      }
      handleError(error, 'UsersManagement.loadUsers');
      // Set empty state on error
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await apiClient.getAdminGroups({ skip: 0, limit: PAGINATION.LARGE_PAGE_SIZE });
      if (!response.error && response.data) {
        const data = response.data as { user_groups: UserGroup[] };
        setGroups(data.user_groups || []);
      }
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      phone_number: '',
      user_group_id: undefined,
      is_active: true,
      phone_verified: false,
    });
    setShowModal(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      phone_number: user.phone_number || '',
      user_group_id: user.user_group_id,
      is_active: user.is_active,
      phone_verified: user.phone_verified,
    });
    setShowModal(true);
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const response = await apiClient.deleteAdminUser(userId);
      if (response.error) {
        throw new AppError(response.error, 'DELETE_USER_FAILED');
      }
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      handleError(error, 'UsersManagement.handleDelete');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingUser) {
        const response = await apiClient.updateAdminUser(editingUser.id, {
          email: formData.email,
          phone_number: formData.phone_number || undefined,
          user_group_id: formData.user_group_id,
          is_active: formData.is_active,
          phone_verified: formData.phone_verified,
        });

        if (response.error) {
          throw new AppError(response.error, 'UPDATE_USER_FAILED');
        }
        toast.success('User updated successfully');
        setShowModal(false);
        loadUsers();
      } else {
        const response = await apiClient.createAdminUser({
          email: formData.email,
          phone_number: formData.phone_number || undefined,
          user_group_id: formData.user_group_id,
          is_active: formData.is_active,
        });

        if (response.error) {
          throw new AppError(response.error, 'CREATE_USER_FAILED');
        }
        toast.success('User created successfully');
        setShowModal(false);
        loadUsers();
      }
    } catch (error) {
      handleError(error, 'UsersManagement.handleSubmit');
    }
  };

  const totalPages = Math.ceil(totalUsers / pageSize);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Users</h2>
        <Button onClick={handleCreate} variant="primary">
          Create User
        </Button>
      </div>

      <InputGroup className="mb-3">
        <Form.Control
          type="text"
          placeholder="Search users by email..."
          value={searchTerm}
          maxLength={INPUT_LIMITS.SEARCH_MAX_LENGTH}
          onChange={(e) => {
            // Validate and sanitize search input
            const value = e.target.value
              .substring(0, INPUT_LIMITS.SEARCH_MAX_LENGTH) // Limit length
              .replace(/[<>]/g, ''); // Remove potentially dangerous characters

            setSearchTerm(value);
            setCurrentPage(0);
          }}
        />
      </InputGroup>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : users.length === 0 ? (
        <Alert variant="info">No users found</Alert>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Group</th>
                <th>Status</th>
                <th>Phone Verified</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const group = groups.find((g) => g.id === user.user_group_id);
                return (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.email}</td>
                    <td>{user.phone_number || '-'}</td>
                    <td>{group?.name || '-'}</td>
                    <td>
                      <Badge bg={user.is_active ? BADGE_COLORS.ACTIVE : BADGE_COLORS.INACTIVE}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={user.phone_verified ? BADGE_COLORS.VERIFIED : BADGE_COLORS.UNVERIFIED}>
                        {user.phone_verified ? 'Verified' : 'Not Verified'}
                      </Badge>
                    </td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => handleEdit(user)} className="me-2">
                        Edit
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(user.id)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="d-flex justify-content-between align-items-center mt-4">
            <div>
              Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalUsers)} of {totalUsers} users
            </div>
            <div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="me-2"
              >
                Previous
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingUser ? 'Edit User' : 'Create User'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="tel"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>User Group</Form.Label>
              <Form.Select
                value={formData.user_group_id || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    user_group_id: e.target.value ? parseInt(e.target.value, 10) : undefined,
                  })
                }
              >
                <option value="">None</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </Form.Group>

            {editingUser && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Phone Verified"
                  checked={formData.phone_verified}
                  onChange={(e) => setFormData({ ...formData, phone_verified: e.target.checked })}
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
